import { createDocument, setDocFolder } from './library';
import { normalizeFolderPath } from './folders';
import { unzip, ZipError } from './unzip';
import { convertDocx, convertHtml, isDocxFile, isHtmlFile } from './convertImport';
import { putImage } from './images';
import { enexNoteToMarkdown, isEnexFile, parseEnex, type EnexResource } from './importers/enex';
import {
  cleanNotionPath,
  liftNotionProperties,
  looksLikeNotionExport,
  notionCsvToMarkdown,
  notionTitleFromPath,
  rewriteNotionLinks,
  type NotionContext,
} from './importers/notion';

const MD_EXT = /\.(md|markdown|mdown|mkd|mdwn|txt)$/i;

/** Whether a file looks like Markdown/plain text we can import. */
export function isMarkdownFile(file: File): boolean {
  return MD_EXT.test(file.name) || file.type === 'text/markdown' || file.type === 'text/plain';
}

/** Markdown and text, or a Word or HTML document converted on the way in. */
export function isImportableFile(file: File): boolean {
  return isMarkdownFile(file) || isHtmlFile(file) || isDocxFile(file);
}

/**
 * Read a file's bytes. Uses Blob.arrayBuffer where it exists and FileReader
 * where it does not — jsdom, which the unit tests run in, has only the latter, and
 * the import path is worth testing for real rather than around.
 */
function readBytes(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function readText(file: Blob): Promise<string> {
  return new TextDecoder().decode(await readBytes(file));
}

/** What one file turns into: a title, the Markdown, and when it was written. */
export interface ImportedDocument {
  title: string;
  content: string;
  /** The note's original creation date, where the export carried one. */
  createdAt?: number | null;
}

/** A file to import, with the library folder it should land in. */
export interface ImportItem {
  file: File;
  folder: string | null;
  /**
   * Produce the document instead of reading the file as Markdown. Used by the
   * migration paths, where one file is many notes (Evernote) or a note only
   * makes sense with the rest of the export in hand (Notion).
   */
  convert?: () => Promise<ImportedDocument>;
}

export function isZipFile(file: File): boolean {
  return (
    /\.zip$/i.test(file.name) ||
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed'
  );
}

/** Operating-system litter that rides along in zipped folders. */
function isJunkPath(path: string): boolean {
  return path.split('/').some((part) => part === '__MACOSX' || part.startsWith('.'));
}

/** What a drop or selection turns into once archives are opened. */
export interface PreparedImport {
  accepted: ImportItem[];
  /** Display names of files that are not text — `archive.zip › photo.png` for archive entries. */
  rejected: string[];
  /** Archives that could not be read, with the reason's error code. */
  unreadable: { name: string; code: ZipError['code'] }[];
}

function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}

const IMAGE_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  avif: 'image/avif',
  bmp: 'image/bmp',
};

function imageType(path: string): string | null {
  return IMAGE_EXT[path.slice(path.lastIndexOf('.') + 1).toLowerCase()] ?? null;
}

/** Keep a picture from an export; anything else keeps its link as written. */
async function storeImageBlob(blob: Blob): Promise<string | null> {
  return blob.type.startsWith('image/') ? await putImage(blob) : null;
}

/**
 * One Evernote export, as one note per file.
 *
 * The `.enex` file's own name becomes the folder, because that is the notebook
 * it was exported from and it is the only grouping the format preserves.
 */
async function enexItems(file: File): Promise<ImportItem[]> {
  const notes = parseEnex(await readText(file));
  const folder = normalizeFolderPath(baseName(file.name));
  return notes.map((note) => ({
    file: new File([''], `${note.title}.md`, { type: 'text/markdown' }),
    folder,
    convert: () =>
      enexNoteToMarkdown(note, async (resource: EnexResource) => {
        const blob = new Blob([resource.data as BlobPart], { type: resource.mime });
        return (await storeImageBlob(blob)) ?? '';
      }),
  }));
}

const NOTION_MD = /\.(md|markdown)$/i;
const NOTION_CSV = /\.csv$/i;

/**
 * A whole Notion export, converted with the rest of the export in view: the
 * page titles are needed to turn links between pages into wiki links, and the
 * images are needed to store what those pages show.
 */
function notionItems(entries: { path: string; data: Uint8Array<ArrayBuffer> }[]): ImportItem[] {
  const pages = new Map<string, string>();
  const assets = new Map<string, Blob>();
  for (const entry of entries) {
    if (NOTION_MD.test(entry.path) || NOTION_CSV.test(entry.path)) {
      pages.set(entry.path, notionTitleFromPath(entry.path));
    } else {
      const type = imageType(entry.path);
      if (type) assets.set(entry.path, new Blob([entry.data as BlobPart], { type }));
    }
  }
  const titles = new Set(pages.values());
  const context: NotionContext = {
    pages,
    assets,
    storeAsset: async (blob) => (await storeImageBlob(blob)) ?? '',
  };

  const items: ImportItem[] = [];
  for (const entry of entries) {
    const isMarkdown = NOTION_MD.test(entry.path);
    if (!isMarkdown && !NOTION_CSV.test(entry.path)) continue;
    // Notion writes a database twice: the view, and `…_all.csv` with every row.
    // Importing both would give the same table under the same name.
    if (/_all\.csv$/i.test(entry.path) && pages.has(entry.path.replace(/_all\.csv$/i, '.csv'))) {
      continue;
    }

    const title = notionTitleFromPath(entry.path);
    const clean = cleanNotionPath(entry.path);
    const slash = clean.lastIndexOf('/');
    const file = new File([entry.data as BlobPart], clean.slice(slash + 1), {
      type: 'text/markdown',
    });
    items.push({
      file,
      folder: normalizeFolderPath(slash > 0 ? clean.slice(0, slash) : null),
      convert: async () => {
        const text = await readText(file);
        if (!isMarkdown) return { title, content: notionCsvToMarkdown(text, title, titles) };
        return {
          title,
          content: liftNotionProperties(await rewriteNotionLinks(text, entry.path, context)),
        };
      },
    });
  }
  return items;
}

/**
 * Open any `.zip` among the files, keep their folder structure, and split the
 * result into importable and not.
 *
 * Nothing is dropped silently: whatever cannot be imported comes back in
 * `rejected` or `unreadable`, so the caller can say what it ignored — dragging a
 * PDF onto the app used to do nothing at all, with no sign it had been noticed.
 *
 * The library export is a zip, so without this an export could never come back
 * in. Folders inside the archive become library folders: zipping a folder of
 * notes is the common way to move a whole set, and flattening it would lose the
 * one structure the user already had.
 */
export async function prepareImport(files: FileList | File[]): Promise<PreparedImport> {
  const result: PreparedImport = { accepted: [], rejected: [], unreadable: [] };

  for (const file of Array.from(files)) {
    if (isEnexFile(file)) {
      try {
        result.accepted.push(...(await enexItems(file)));
      } catch {
        result.unreadable.push({ name: file.name, code: 'invalid' });
      }
      continue;
    }

    if (!isZipFile(file)) {
      if (isImportableFile(file)) result.accepted.push({ file, folder: null });
      else result.rejected.push(file.name);
      continue;
    }

    let entries;
    try {
      entries = await unzip(await readBytes(file));
    } catch (err) {
      result.unreadable.push({
        name: file.name,
        code: err instanceof ZipError ? err.code : 'invalid',
      });
      continue;
    }

    const usable = entries.filter((entry) => !isJunkPath(entry.path));
    if (looksLikeNotionExport(usable.map((entry) => entry.path))) {
      result.accepted.push(...notionItems(usable));
      continue;
    }

    for (const entry of usable) {
      const slash = entry.path.lastIndexOf('/');
      const name = entry.path.slice(slash + 1);
      const entryFile = new File([entry.data], name);
      if (isImportableFile(entryFile)) {
        result.accepted.push({
          file: entryFile,
          folder: normalizeFolderPath(slash > 0 ? entry.path.slice(0, slash) : null),
        });
      } else {
        result.rejected.push(`${file.name} › ${entry.path}`);
      }
    }
  }

  return result;
}

/** What an import produced. */
export interface ImportResult {
  /** Ids of the created documents, in order. */
  ids: string[];
  /** Names of files that were importable by type but could not be converted. */
  failed: string[];
}

async function readDocument(item: ImportItem): Promise<ImportedDocument> {
  if (item.convert) return item.convert();
  const file = item.file;
  if (isDocxFile(file)) return convertDocx(await readBytes(file), file.name);
  if (isHtmlFile(file)) return convertHtml(await readText(file), file.name);
  return { title: file.name.replace(MD_EXT, '') || 'Imported', content: await readText(file) };
}

/**
 * Import files into the local library, one document per file, each into its
 * folder when it has one. Markdown and text come in as they are; Word and HTML
 * are converted first. Unsupported files are skipped; a file that fails to
 * convert — a damaged `.docx`, say — is reported and does not stop the rest.
 */
export async function importMarkdownFiles(
  items: FileList | ReadonlyArray<File | ImportItem>,
): Promise<ImportResult> {
  const result: ImportResult = { ids: [], failed: [] };
  for (const item of Array.from(items)) {
    const entry: ImportItem = item instanceof File ? { file: item, folder: null } : item;
    const { file, folder } = entry;
    // A converter knows what it is holding; without one the file has to look
    // like something we can read.
    if (!entry.convert && !isImportableFile(file)) continue;
    let converted;
    try {
      converted = await readDocument(entry);
    } catch {
      result.failed.push(file.name);
      continue;
    }
    const doc = await createDocument({
      title: converted.title,
      content: converted.content,
      createdAt: converted.createdAt ?? undefined,
    });
    if (folder) await setDocFolder(doc.id, folder);
    result.ids.push(doc.id);
  }
  return result;
}

/**
 * Import a Markdown/text document from a public URL via the same-origin API
 * proxy (avoids CORS; the server enforces SSRF/size/type guards). Returns the
 * new document's id, or throws an Error with a user-facing message.
 */
export async function importFromUrl(url: string): Promise<string> {
  const res = await fetch('/api/import', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Import failed (${res.status}).`);
  }
  const { title, content } = (await res.json()) as { title: string; content: string };
  const doc = await createDocument({ title, content });
  return doc.id;
}
